
import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Settings, Trash2, Edit } from 'lucide-react';
import { useProactiveInsights } from '@/hooks/useProactiveInsights';

export const AlertRulesManager = () => {
  const { alertRules, createAlertRule, isLoading, loadAlertRules } = useProactiveInsights();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ruleType: 'threshold' as 'threshold' | 'trend' | 'anomaly' | 'pattern',
    dataSource: '',
    conditions: {},
    alertFrequency: 'immediate' as 'immediate' | 'daily' | 'weekly',
    deliveryMethods: ['notification'],
    isActive: true
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    await createAlertRule(formData);
    setIsDialogOpen(false);
    setFormData({
      name: '',
      description: '',
      ruleType: 'threshold',
      dataSource: '',
      conditions: {},
      alertFrequency: 'immediate',
      deliveryMethods: ['notification'],
      isActive: true
    });
  };

  const getRuleTypeColor = (type: string) => {
    switch (type) {
      case 'threshold':
        return 'bg-blue-100 text-blue-800';
      case 'trend':
        return 'bg-green-100 text-green-800';
      case 'anomaly':
        return 'bg-orange-100 text-orange-800';
      case 'pattern':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Settings className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-xl font-semibold">Alert Rules</h2>
            <p className="text-sm text-muted-foreground">
              Configure automatic insight generation rules
            </p>
          </div>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Create Rule
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Alert Rule</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Rule Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., High Revenue Alert"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe when this rule should trigger..."
                />
              </div>
              
              <div>
                <Label htmlFor="ruleType">Rule Type</Label>
                <Select 
                  value={formData.ruleType} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, ruleType: value as any }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="threshold">Threshold</SelectItem>
                    <SelectItem value="trend">Trend</SelectItem>
                    <SelectItem value="anomaly">Anomaly</SelectItem>
                    <SelectItem value="pattern">Pattern</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="dataSource">Data Source</Label>
                <Select 
                  value={formData.dataSource} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, dataSource: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select data source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="finance_data">Finance Data</SelectItem>
                    <SelectItem value="ecommerce_data">E-commerce Data</SelectItem>
                    <SelectItem value="tech_data">Tech Data</SelectItem>
                    <SelectItem value="logistics_data">Logistics Data</SelectItem>
                    <SelectItem value="realestate_data">Real Estate Data</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="alertFrequency">Alert Frequency</Label>
                <Select 
                  value={formData.alertFrequency} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, alertFrequency: value as any }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">Immediate</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked }))}
                />
                <Label htmlFor="isActive">Active</Label>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  Create Rule
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <ScrollArea className="h-64">
        <div className="space-y-3">
          {alertRules.length === 0 ? (
            <div className="text-center py-8">
              <Settings className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Alert Rules</h3>
              <p className="text-muted-foreground">
                Create alert rules to automatically generate insights when data patterns change
              </p>
            </div>
          ) : (
            alertRules.map((rule) => (
              <Card key={rule.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`px-2 py-1 rounded text-xs font-medium ${getRuleTypeColor(rule.ruleType)}`}>
                      {rule.ruleType}
                    </div>
                    <div>
                      <h3 className="font-medium">{rule.name}</h3>
                      <p className="text-sm text-muted-foreground">{rule.description}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <span>Source: {rule.dataSource}</span>
                        <span>•</span>
                        <span>Frequency: {rule.alertFrequency}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Badge variant={rule.isActive ? "default" : "secondary"}>
                      {rule.isActive ? "Active" : "Inactive"}
                    </Badge>
                    <Button variant="ghost" size="sm">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </ScrollArea>
    </Card>
  );
};
